import { Component, computed, inject, signal } from "@angular/core"
import { DatePipe } from "@angular/common"
import { MatIconModule } from "@angular/material/icon"
import { MatTooltipModule } from "@angular/material/tooltip"
import { catchError, finalize, of, takeUntil, Subscription } from "rxjs"
import { AppBarComponent } from "../../../../shared/components/app-bar/app-bar.component"
import { MainContentComponent } from "../../../../shared/components/main-content/main-content.component"
import { SeoComponent } from "../../../../shared/components/seo/seo.component"
import { UUID } from "../../../../test/constants/strings"
import {
  IAggregateInsightsResponse,
  IPersonalInsightTest,
  InsightPeriod,
} from "../../../interfaces/insight.interface"
import { InsightService } from "../../../services/insight.service"

type InsightView = "personal" | "aggregate"

@Component({
  selector: "app-insight",
  imports: [DatePipe, MatIconModule, MatTooltipModule, AppBarComponent, MainContentComponent],
  templateUrl: "./insight.component.html",
  styleUrl: "./insight.component.scss",
})
export class InsightComponent extends SeoComponent {
  private readonly insightService = inject(InsightService)
  private readonly pageSize = 20
  private aggregateRequest?: Subscription

  readonly activeView = signal<InsightView>("personal")
  readonly activePeriod = signal<InsightPeriod>("daily")
  readonly personalTests = signal<IPersonalInsightTest[]>([])
  readonly personalTotal = signal(0)
  readonly personalLoading = signal(false)
  readonly personalError = signal(false)
  readonly aggregate = signal<IAggregateInsightsResponse | null>(null)
  readonly aggregateLoading = signal(false)
  readonly aggregateError = signal(false)

  readonly clientId = globalThis.localStorage?.getItem(UUID) ?? null
  readonly hasMorePersonal = computed(
    () => this.personalTests().length < this.personalTotal(),
  )
  readonly hasTimelineData = computed(() => this.aggregate()?.timeline.some((item) => item.test_count > 0) ?? false)
  readonly chartMax = computed(() => {
    const values = this.aggregate()?.timeline.flatMap((item) => [
      item.download.max ?? 0,
      item.upload.max ?? 0,
    ]) ?? []
    return Math.max(1, ...values)
  })

  readonly periodFilters: { id: InsightPeriod; label: string }[] = [
    { id: "daily", label: "Daily" },
    { id: "weekly", label: "Weekly" },
    { id: "monthly", label: "Monthly" },
  ]

  constructor() {
    super()
    this.title = "Insight"
    const snapshot = this.clientId ? this.insightService.getPersonalSnapshot(this.clientId) : null
    if (snapshot) {
      this.personalTests.set(snapshot.tests)
      this.personalTotal.set(snapshot.total)
    }
    this.loadPersonal(true)
  }

  setView(view: InsightView): void {
    this.activeView.set(view)
    if (view === "aggregate" && !this.aggregate()) this.loadAggregate()
  }

  setPeriod(period: InsightPeriod): void {
    if (period === this.activePeriod()) return
    this.activePeriod.set(period)
    this.loadAggregate()
  }

  loadPersonal(reset = false): void {
    if (!this.clientId || this.personalLoading()) return
    this.personalLoading.set(true)
    this.personalError.set(false)
    this.insightService
      .getPersonal(this.clientId, this.pageSize, reset ? 0 : this.personalTests().length)
      .pipe(
        catchError(() => {
          this.personalError.set(true)
          return of(null)
        }),
        finalize(() => this.personalLoading.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((response) => {
        if (!response) return
        this.personalTotal.set(response.total)
        if (reset) this.personalTests.set(response.tests)
        else this.personalTests.update((tests) => [...tests, ...response.tests])
      })
  }

  loadAggregate(): void {
    this.aggregateRequest?.unsubscribe()
    this.aggregateLoading.set(true)
    this.aggregateError.set(false)
    this.aggregateRequest = this.insightService
      .getAggregate(this.activePeriod())
      .pipe(
        catchError(() => {
          this.aggregateError.set(true)
          return of(null)
        }),
        finalize(() => this.aggregateLoading.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((response) => this.aggregate.set(response))
  }

  barTooltip(metric: string, value: number | null | undefined, dateRange: string, testCount: number): string {
    const speed = value == null ? "No data" : `${value} Mbps`
    return `${metric}: ${speed} · ${dateRange} · ${testCount} ${testCount === 1 ? "test" : "tests"}`
  }

  barHeight(value: number | null | undefined): number {
    return Math.max(0, ((value ?? 0) / this.chartMax()) * 100)
  }

  confidenceLabel(value: number): "High" | "Medium" | "Low" {
    return value >= 0.75 ? "High" : value >= 0.4 ? "Medium" : "Low"
  }

  formatSpeed(value: number | null | undefined): string {
    return value == null ? "—" : value.toFixed(1)
  }
}
